import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useSearchParams, Link } from "react-router-dom";
import { Package, CheckCircle, Plus, Trash2, Save, FileText, Calendar, Clock, XCircle, Check, Receipt, Edit, Download, X } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import useAutoRefresh from "../../hooks/useAutoRefresh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const AdminBookings = () => {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const agentFilter = searchParams.get("agent_id") || "";
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(agentFilter ? "all" : "upcoming");
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmations, setConfirmations] = useState([]);
  
  // Invoice state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    invoice_number: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    guest_name: "",
    guest_address: "",
    guest_phone: "",
    guest_email: "",
    items: [],
    subtotal: 0,
    tax_rate: 5,
    tax_amount: 0,
    discount: 0,
    total: 0,
    notes: "",
    payment_terms: "Payment due within 7 days",
    bank_details: ""
  });

  useEffect(() => {
    fetchBookings();
  }, []);

  // Auto-refresh (~1s) — new bookings surface instantly
  useAutoRefresh(() => fetchBookings());

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter bookings based on active tab
  const getFilteredBookings = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (activeTab) {
      case "upcoming":
        // Confirmed bookings that are upcoming OR currently ongoing
        // (return date >= today => tour not yet ended). Falls back to travel_date
        // when return_date is missing so legacy data still surfaces.
        return bookings.filter(b => {
          if (b.status !== "confirmed") return false;
          const endStr = b.return_date || b.travel_date;
          if (!endStr) return true; // keep visible if no dates set
          const endDate = new Date(endStr);
          endDate.setHours(0, 0, 0, 0);
          return endDate >= today;
        }).sort((a, b) => new Date(a.travel_date || 0) - new Date(b.travel_date || 0));

      case "pending":
        // Pending bookings (not yet confirmed)
        return bookings.filter(b => b.status === "pending");

      case "cancelled":
        // Cancelled bookings
        return bookings.filter(b => b.status === "cancelled");

      case "completed":
        // Confirmed bookings with travel date in past
        return bookings.filter(b => {
          if (b.status !== "confirmed") return false;
          const returnDate = new Date(b.return_date || b.travel_date);
          return returnDate < today;
        }).sort((a, b) => new Date(b.travel_date) - new Date(a.travel_date));

      case "invoice":
        // Bookings on last day of travel or completed - ready for invoice
        return bookings.filter(b => {
          if (b.status !== "confirmed") return false;
          const returnDate = new Date(b.return_date || b.travel_date);
          returnDate.setHours(0, 0, 0, 0);
          // Include if return date is today or in the past
          return returnDate <= today;
        }).sort((a, b) => new Date(b.return_date || b.travel_date) - new Date(a.return_date || a.travel_date));

      default:
        // Sort by Booking Date ascending (01 May first, then 02 May, etc.)
        return [...bookings].sort((a, b) => new Date(a.created_at || a.booking_date || 0) - new Date(b.created_at || b.booking_date || 0));
    }
  };

  // Get counts for tabs
  const getCounts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = bookings.filter(b => {
      if (b.status !== "confirmed") return false;
      const endStr = b.return_date || b.travel_date;
      if (!endStr) return true;
      const endDate = new Date(endStr);
      endDate.setHours(0, 0, 0, 0);
      return endDate >= today;
    }).length;

    const pending = bookings.filter(b => b.status === "pending").length;
    const cancelled = bookings.filter(b => b.status === "cancelled").length;

    const completed = bookings.filter(b => {
      if (b.status !== "confirmed") return false;
      const returnDate = new Date(b.return_date || b.travel_date);
      return returnDate < today;
    }).length;

    // Invoice ready - confirmed bookings on or past return date
    const invoiceReady = bookings.filter(b => {
      if (b.status !== "confirmed") return false;
      const returnDate = new Date(b.return_date || b.travel_date);
      returnDate.setHours(0, 0, 0, 0);
      return returnDate <= today;
    }).length;

    return { upcoming, pending, cancelled, completed, invoiceReady };
  };

  const counts = getCounts();
  // Apply ?agent_id= URL filter on top of the tab filter
  const filteredBookings = useMemo(() => {
    const rows = getFilteredBookings();
    if (!agentFilter) return rows;
    return rows.filter(b => b.agent_id === agentFilter);
  }, [bookings, activeTab, agentFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve agent display name from the filtered set (any row will have it)
  const agentLabel = useMemo(() => {
    if (!agentFilter) return "";
    if (agentFilter === "DIRECT") return "Direct Client";
    const hit = bookings.find(b => b.agent_id === agentFilter);
    return hit?.agent_company || hit?.agent_user_name || hit?.agent_name || "Agent";
  }, [bookings, agentFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Booking status updated!");
      fetchBookings();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update booking status");
    }
  };

  const updatePayment = async (id, status) => {
    try {
      await axios.put(`${API}/bookings/${id}/payment?payment_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment status updated!");
      fetchBookings();
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error("Failed to update payment status");
    }
  };

  const deleteBooking = async (id, ref) => {
    if (!window.confirm(`Permanently delete cancelled booking #${ref || id.slice(0,8).toUpperCase()}? This will also remove its payments, vendor payments, invoices, and follow-ups. This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Booking deleted!");
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    }
  };

  // Invoice functions
  const openInvoiceModal = (booking) => {
    setSelectedBooking(booking);
    
    // Generate invoice number
    const invNumber = `INV-${booking.id.slice(0, 8).toUpperCase()}-${Date.now().toString().slice(-4)}`;
    
    // Build invoice items from booking details
    const items = [];
    
    // Add package cost
    items.push({
      description: `${booking.destination} Tour Package (${booking.pax_adults} Adults${booking.pax_children > 0 ? `, ${booking.pax_children} Children` : ''})`,
      quantity: 1,
      rate: booking.final_amount || booking.quote_amount || 0,
      amount: booking.final_amount || booking.quote_amount || 0
    });
    
    // Calculate totals
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const taxAmount = Math.round(subtotal * 0.05);
    const total = subtotal + taxAmount;
    
    setInvoiceData({
      invoice_number: invNumber,
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      guest_name: booking.guest_name || booking.client_name || "",
      guest_address: booking.guest_address || "",
      guest_phone: booking.client_phone || "",
      guest_email: booking.client_email || "",
      items: items,
      subtotal: subtotal,
      tax_rate: 5,
      tax_amount: taxAmount,
      discount: 0,
      total: total,
      notes: `Travel Dates: ${formatDate(booking.travel_date)} to ${formatDate(booking.return_date)}`,
      payment_terms: "Payment due within 7 days of invoice date",
      bank_details: "Bank: HDFC Bank\nAccount Name: Happy Holidays DMC\nAccount No: XXXXXXXXXX\nIFSC: HDFC0001234"
    });
    
    setShowInvoiceModal(true);
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'rate') {
      newItems[index].amount = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].rate) || 0);
    }
    
    const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = Math.round(subtotal * (invoiceData.tax_rate / 100));
    const total = subtotal + taxAmount - (parseFloat(invoiceData.discount) || 0);
    
    setInvoiceData({
      ...invoiceData,
      items: newItems,
      subtotal,
      tax_amount: taxAmount,
      total
    });
  };

  const addInvoiceItem = () => {
    const newItems = [...invoiceData.items, { description: "", quantity: 1, rate: 0, amount: 0 }];
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const removeInvoiceItem = (index) => {
    const newItems = invoiceData.items.filter((_, i) => i !== index);
    const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = Math.round(subtotal * (invoiceData.tax_rate / 100));
    const total = subtotal + taxAmount - (parseFloat(invoiceData.discount) || 0);
    
    setInvoiceData({
      ...invoiceData,
      items: newItems,
      subtotal,
      tax_amount: taxAmount,
      total
    });
  };

  const recalculateTotals = (newData) => {
    const subtotal = newData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const taxAmount = Math.round(subtotal * ((parseFloat(newData.tax_rate) || 0) / 100));
    const total = subtotal + taxAmount - (parseFloat(newData.discount) || 0);
    return { ...newData, subtotal, tax_amount: taxAmount, total };
  };

  const downloadInvoicePDF = () => {
    const doc = new jsPDF();
    let y = 20;
    
    // Header
    doc.setFontSize(24);
    doc.setTextColor(16, 185, 129);
    doc.text("INVOICE", 105, y, { align: "center" });
    y += 15;
    
    // Invoice details
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Invoice No: ${invoiceData.invoice_number}`, 150, y);
    y += 6;
    doc.text(`Date: ${formatDate(invoiceData.invoice_date)}`, 150, y);
    y += 6;
    doc.text(`Due Date: ${formatDate(invoiceData.due_date)}`, 150, y);
    y += 15;
    
    // Company Info (Left)
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Happy Holidays DMC", 20, 35);
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("Travel & Tourism Services", 20, 41);
    
    // Bill To
    y = 70;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text("Bill To:", 20, y);
    y += 7;
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(invoiceData.guest_name || "-", 20, y);
    y += 5;
    if (invoiceData.guest_address) doc.text(invoiceData.guest_address, 20, y);
    y += 5;
    if (invoiceData.guest_phone) doc.text(`Phone: ${invoiceData.guest_phone}`, 20, y);
    y += 5;
    if (invoiceData.guest_email) doc.text(`Email: ${invoiceData.guest_email}`, 20, y);
    y += 15;
    
    // Table Header
    doc.setFillColor(240, 240, 240);
    doc.rect(20, y, 170, 10, 'F');
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Description", 25, y + 7);
    doc.text("Qty", 120, y + 7);
    doc.text("Rate", 140, y + 7);
    doc.text("Amount", 170, y + 7);
    y += 15;
    
    // Table Items
    doc.setTextColor(80, 80, 80);
    invoiceData.items.forEach(item => {
      const desc = doc.splitTextToSize(item.description || "", 90);
      doc.text(desc, 25, y);
      doc.text(String(item.quantity), 120, y);
      doc.text(`₹${(item.rate || 0).toLocaleString()}`, 140, y);
      doc.text(`₹${(item.amount || 0).toLocaleString()}`, 170, y);
      y += desc.length * 5 + 5;
    });
    
    // Totals
    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(120, y, 190, y);
    y += 8;
    
    doc.text("Subtotal:", 130, y);
    doc.text(`₹${invoiceData.subtotal.toLocaleString()}`, 170, y);
    y += 7;
    
    doc.text(`Tax (${invoiceData.tax_rate}%):`, 130, y);
    doc.text(`₹${invoiceData.tax_amount.toLocaleString()}`, 170, y);
    y += 7;
    
    if (invoiceData.discount > 0) {
      doc.text("Discount:", 130, y);
      doc.text(`-₹${invoiceData.discount.toLocaleString()}`, 170, y);
      y += 7;
    }
    
    doc.setFontSize(12);
    doc.setTextColor(16, 185, 129);
    doc.text("TOTAL:", 130, y);
    doc.text(`₹${invoiceData.total.toLocaleString()}`, 170, y);
    
    // Notes
    if (invoiceData.notes) {
      y += 20;
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Notes:", 20, y);
      y += 6;
      const notes = doc.splitTextToSize(invoiceData.notes, 170);
      doc.text(notes, 20, y);
    }
    
    // Bank Details
    if (invoiceData.bank_details) {
      y += 20;
      doc.text("Bank Details:", 20, y);
      y += 6;
      const bankLines = invoiceData.bank_details.split('\n');
      bankLines.forEach(line => {
        doc.text(line, 20, y);
        y += 5;
      });
    }
    
    // Save
    doc.save(`${invoiceData.invoice_number}.pdf`);
    toast.success("Invoice PDF downloaded!");
  };

  const openConfirmModal = (booking) => {
    setSelectedBooking(booking);
    setConfirmations(booking.confirmations || [{
      city: "", hotel: "", check_in: "", check_out: "",
      rooms: 1, category: "", meal_plan: "CP", confirmation_number: ""
    }]);
    setShowConfirmModal(true);
  };

  const handleAddConfirmation = () => {
    setConfirmations([...confirmations, {
      city: "", hotel: "", check_in: "", check_out: "",
      rooms: 1, category: "", meal_plan: "CP", confirmation_number: ""
    }]);
  };

  const handleRemoveConfirmation = (index) => {
    const newConf = [...confirmations];
    newConf.splice(index, 1);
    setConfirmations(newConf);
  };

  const handleConfirmationChange = (index, field, value) => {
    const newConf = [...confirmations];
    newConf[index][field] = value;
    setConfirmations(newConf);
  };

  const saveConfirmations = async () => {
    try {
      await axios.put(`${API}/bookings/${selectedBooking.id}/confirmations`, confirmations, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Confirmations saved!");
      setShowConfirmModal(false);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to save confirmations");
    }
  };

  const tabs = [
    { id: "upcoming", label: "Upcoming", count: counts.upcoming, icon: Calendar, color: "text-blue-600", bg: "bg-blue-50" },
    { id: "pending", label: "Pending", count: counts.pending, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
    { id: "cancelled", label: "Cancelled", count: counts.cancelled, icon: XCircle, color: "text-red-600", bg: "bg-red-50" },
    { id: "completed", label: "Completed", count: counts.completed, icon: Check, color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      confirmed: "bg-emerald-100 text-emerald-700",
      cancelled: "bg-red-100 text-red-700",
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      partial: "bg-orange-100 text-orange-700",
      full: "bg-emerald-100 text-emerald-700",
    };
    return styles[status] || "bg-gray-100 text-gray-700";
  };

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-bookings">
        {/* Header */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          </div>
          <p className="text-gray-500">Manage booking confirmations and payments</p>
        </div>

        {/* Agent filter banner (set via ?agent_id= URL param from Top Agents leaderboard) */}
        {agentFilter && (
          <div
            className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
            data-testid="agent-filter-banner"
          >
            <div className="flex items-center gap-3 text-sm">
              <span className="rounded-md bg-amber-200/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                Filtered
              </span>
              <span className="text-gray-700">
                Showing bookings of{" "}
                <span className="font-semibold text-gray-900">{agentLabel}</span>
                {" "}
                ({filteredBookings.length} {filteredBookings.length === 1 ? "booking" : "bookings"})
              </span>
            </div>
            <Link
              to="/admin/bookings"
              onClick={() => { setSearchParams({}); setActiveTab("upcoming"); }}
              className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100"
              data-testid="clear-agent-filter"
            >
              <X className="w-3.5 h-3.5" /> Clear filter
            </Link>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                activeTab === tab.id
                  ? `${tab.bg} ${tab.color} shadow-sm`
                  : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
              <span className={`px-2 py-0.5 text-xs rounded-full ${
                activeTab === tab.id ? "bg-white/60" : "bg-gray-100"
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Bookings Table */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No {activeTab} bookings found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agent Detail</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client Detail</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Destination</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Travel Date</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Booking Amt</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Paid Amt</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance Amt</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredBookings.map((booking) => {
                      const a = Number(booking.pax_adults || 0);
                      const cwb = Number(booking.cwb || 0);
                      const cnb = Number(booking.cnb || 0);
                      const inf = Number(booking.pax_infants || 0);
                      const totalChildren = cwb + cnb + inf;
                      const paxStr = totalChildren > 0 ? `${a}A + ${totalChildren}C` : `${a} ${a === 1 ? 'Adult' : 'Adults'}`;
                      return (
                      <tr key={booking.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <a 
                            href={`/admin/bookings/${booking.id}`}
                            className="font-mono text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded text-sm hover:bg-emerald-100 hover:text-emerald-700 transition-colors cursor-pointer"
                            data-testid={`booking-id-${booking.id}`}
                          >
                            #{booking.booking_number || booking.id.slice(0, 8).toUpperCase()}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700 text-sm">{formatDate(booking.created_at || booking.booking_date)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-800 font-medium text-sm">{booking.agent_company || '-'}</p>
                          <p className="text-gray-400 text-xs">{booking.agent_user_name || booking.agent_name || '-'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-800 font-medium text-sm">{booking.guest_name || booking.client_name || '-'}</p>
                          <p className="text-gray-500 text-xs mt-0.5">{paxStr}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-gray-800 font-medium text-sm">{booking.destination}</p>
                          {booking.nights > 0 && (
                            <p className="text-gray-500 text-xs mt-0.5">{booking.nights} {booking.nights === 1 ? 'Night' : 'Nights'}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700 text-sm">{formatDate(booking.travel_date)}</span>
                          </div>
                          {booking.return_date && (
                            <div className="flex items-center gap-1.5 mt-1">
                              <Calendar className="w-3.5 h-3.5 text-gray-300" />
                              <span className="text-gray-400 text-xs">{formatDate(booking.return_date)}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {(() => {
                            const display = Number(booking.total_amount || 0);
                            const gstOn = !!booking.gst_included;
                            return (
                              <>
                                <span className="text-gray-800 font-semibold text-sm" data-testid={`booking-total-${booking.id}`}>₹{display.toLocaleString('en-IN')}</span>
                                {gstOn && <span className="block text-[10px] text-gray-400">incl. 5% GST</span>}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-emerald-600 font-semibold text-sm">₹{Number(booking.amount_paid || 0).toLocaleString('en-IN')}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {Number(booking.balance_amount || 0) > 0 ? (
                            <span className="text-red-400 font-semibold text-sm">₹{Number(booking.balance_amount).toLocaleString('en-IN')}</span>
                          ) : (
                            <span className="text-green-500 font-semibold text-sm">Nil</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={booking.status}
                            onValueChange={(value) => updateStatus(booking.id, value)}
                          >
                            <SelectTrigger className={`w-28 h-8 text-xs font-medium border-0 capitalize ${getStatusBadge(booking.status)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3">
                          <Select
                            value={booking.payment_status}
                            onValueChange={(value) => updatePayment(booking.id, value)}
                          >
                            <SelectTrigger className={`w-24 h-8 text-xs font-medium border-0 capitalize ${getPaymentBadge(booking.payment_status)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="partial">Partial</SelectItem>
                              <SelectItem value="full">Full</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <a
                              href={`/admin/bookings/${booking.id}`}
                              className="inline-flex items-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded-md text-sm font-medium"
                              data-testid={`booking-view-${booking.id}`}
                            >
                              <FileText className="w-4 h-4 mr-1" /> View
                            </a>
                            {activeTab === 'cancelled' && (
                              <button
                                type="button"
                                onClick={() => deleteBooking(booking.id, booking.booking_number)}
                                className="inline-flex items-center text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-md text-sm font-medium"
                                title="Delete this cancelled booking permanently"
                                data-testid={`booking-delete-${booking.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Confirmation Modal */}
        <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
          <DialogContent className="max-w-4xl bg-white border-gray-200 text-gray-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                Booking Confirmation Details
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                {confirmations.map((conf, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Hotel {idx + 1}</h4>
                      <Button variant="ghost" size="icon" onClick={() => handleRemoveConfirmation(idx)} className="text-red-500 hover:text-red-600 hover:bg-red-50 h-6 w-6">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <Label className="text-xs text-gray-500">City</Label>
                        <Input
                          value={conf.city}
                          onChange={(e) => handleConfirmationChange(idx, 'city', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Hotel Name</Label>
                        <Input
                          value={conf.hotel}
                          onChange={(e) => handleConfirmationChange(idx, 'hotel', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                          placeholder="Hotel"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Room Category</Label>
                        <Input
                          value={conf.category}
                          onChange={(e) => handleConfirmationChange(idx, 'category', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                          placeholder="e.g. Deluxe"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Meal Plan</Label>
                        <Input
                          value={conf.meal_plan}
                          onChange={(e) => handleConfirmationChange(idx, 'meal_plan', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                          placeholder="e.g. CP"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Check In</Label>
                        <Input
                          type="date"
                          value={conf.check_in}
                          onChange={(e) => handleConfirmationChange(idx, 'check_in', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Check Out</Label>
                        <Input
                          type="date"
                          value={conf.check_out}
                          onChange={(e) => handleConfirmationChange(idx, 'check_out', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Rooms</Label>
                        <Input
                          type="number"
                          value={conf.rooms}
                          onChange={(e) => handleConfirmationChange(idx, 'rooms', e.target.value)}
                          className="bg-white border-gray-200 h-9 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-emerald-600 font-semibold">Conf. No.</Label>
                        <Input
                          value={conf.confirmation_number}
                          onChange={(e) => handleConfirmationChange(idx, 'confirmation_number', e.target.value)}
                          className="bg-white border-emerald-300 h-9 text-sm"
                          placeholder="Confirmation #"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={handleAddConfirmation} className="w-full border-dashed border-gray-300 text-gray-600 hover:text-gray-800 hover:bg-gray-50">
                <Plus className="w-4 h-4 mr-2" /> Add Hotel Confirmation
              </Button>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="ghost" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
                <Button onClick={saveConfirmations} className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="w-4 h-4 mr-2" /> Save Details
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminBookings;
